// tutorial.js — 入口に立っている案内係と、その案内の段取り
//
// 話しかけると「案内しましょうか」と聞く。はいと答えると、案内係が台の前まで
// 歩き、プレイヤーも一緒に連れていく。あとは打ちかたを順に説明する。
//
// 段の中には「実際に打つまで待つ」ものがある。読ませるだけの説明より、
// 一度やってもらったほうが早い。待つ段は次へのボタンを出さず、条件が
// 満たされたら勝手に進む。

import { t, tList } from './i18n.js';
import { MACHINES } from './machines.js';

/** 案内を最後まで見たかどうかを覚えておく鍵 */
export const TUTORIAL_KEY = 'pachinkorokoro.tutorial.v1';

// 案内に使う台と、その前に立つ位置（ホールのタイル座標）。
// 甘デジ（よく当たる代わりに出玉は小さい）が最初の一台には向いている。
const TARGET = { machine: 'light', stand: [5, 7], guideStand: [6, 7] };

/**
 * where … その段を見せる画面
 * wait  … 満たされるまで次へ進まない条件。無ければ「つぎへ」で進む
 * focus … 指し示す操作（CSS で光らせる）
 */
const STEPS = [
  { id: 'follow', where: 'world', wait: 'arrived' },
  { id: 'inspect', where: 'world', wait: 'seated' },
  { id: 'handle', where: 'game', focus: 'power' },
  { id: 'fire', where: 'game', focus: 'lever', wait: 'launched' },
  { id: 'spin', where: 'game', wait: 'spun' },
  { id: 'pending', where: 'game' },
  { id: 'reach', where: 'game' },
  { id: 'right', where: 'game', focus: 'power' },
  { id: 'prize', where: 'game' },
  { id: 'end', where: 'game' },
];

const esc = (s) => String(s).replace(/[&<>"']/g, (c) =>
  ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

export class Tutorial {
  /**
   * @param {HTMLElement} root  案内板を置く場所
   * @param {object} handlers
   *   world()        World を返す
   *   onSeatMachine(machine) 案内が終わって台に着かせたいとき（今は使わない）
   *   onFinish(done) 案内が終わったとき。done なら最後まで見た
   */
  constructor(root, handlers = {}) {
    this.on = handlers;
    this.step = -1;
    this.arrived = false;
    this.seatedOnce = false;
    this._build(root);
  }

  get active() { return this.step >= 0; }
  get current() { return this.step >= 0 ? STEPS[this.step] : null; }

  _build(root) {
    this.el = document.createElement('div');
    this.el.className = 'coach gone';
    this.el.innerHTML = `
      <span class="coach-who"></span>
      <p class="coach-line"></p>
      <div class="coach-btns">
        <button type="button" class="coach-skip"></button>
        <button type="button" class="coach-next"></button>
      </div>`;
    this.whoEl = this.el.querySelector('.coach-who');
    this.lineEl = this.el.querySelector('.coach-line');
    this.skipEl = this.el.querySelector('.coach-skip');
    this.nextEl = this.el.querySelector('.coach-next');
    // 押したままにしておくと、次にスペースを叩いたときに発射ではなく
    // ボタンがもう一度効いてしまう。押したら焦点を外す。
    this.skipEl.addEventListener('click', () => { this.skipEl.blur(); this.finish(false); });
    this.nextEl.addEventListener('click', () => { this.nextEl.blur(); this._advance(); });
    root.append(this.el);

    // 案内板は画面の上に浮かせている。そのぶん下の画面を押し下げないと
    // 持ち玉や店の名前が隠れてしまう。高さは言語で変わるので測って渡す。
    if (typeof ResizeObserver !== 'undefined') {
      new ResizeObserver(() => this._syncHeight()).observe(this.el);
    }
  }

  _syncHeight() {
    const on = this.active && !this.el.classList.contains('gone');
    const h = on ? this.el.getBoundingClientRect().height : 0;
    document.documentElement.classList.toggle('coaching', on);
    document.documentElement.style.setProperty('--coach-h', `${Math.round(h)}px`);
  }

  // ---------------------------------------------------------------- 入口

  /** 案内係に話しかけられた。案内するかどうかを聞く */
  greet(firstTime) {
    const world = this.on.world?.();
    if (!world) return;
    if (this.active) {                       // 案内の途中なら、やめるか聞く
      world.say(this._name(), this._color(), [t('tutorial.midway')], [
        { label: t('tutorial.keep'), value: 'keep' },
        { label: t('tutorial.stop'), value: 'stop' },
      ], (v) => { if (v === 'stop') this.finish(false); });
      return;
    }
    const lines = tList(firstTime ? 'tutorial.offerFirst' : 'tutorial.offer');
    world.say(this._name(), this._color(), lines, [
      { label: t('tutorial.yes'), value: 'yes' },
      { label: t('tutorial.no'), value: 'no' },
    ], (v) => {
      if (v === 'yes') this.begin();
      else world.say(this._name(), this._color(), [t('tutorial.declined')]);
    });
  }

  /** 案内を始める。まず台の前まで一緒に歩く */
  begin() {
    const world = this.on.world?.();
    if (!world) return;
    this.step = 0;
    this.arrived = false;
    this.seatedOnce = false;
    world.walkPlayerTo(...TARGET.stand);
    world.walkGuideTo(...TARGET.guideStand, () => { this.arrived = true; });
    this._render();
  }

  finish(done) {
    if (!this.active) return;
    this.step = -1;
    this._clearFocus();
    this.el.classList.add('gone');
    this._syncHeight();
    const world = this.on.world?.();
    world?.guideGoHome();
    if (done) world?.say(this._name(), this._color(), [t('tutorial.bye')]);
    this.on.onFinish?.(done);
  }

  // ---------------------------------------------------------------- 進行

  /**
   * 毎フレーム呼ぶ。待ちの条件を見て進め、今の画面に合う段だけ見せる。
   * @param {object} ctx {worldOpen, editorOpen, seated, game}
   */
  tick(ctx) {
    if (!this.active) return;
    const step = this.current;
    if (ctx.seated) this.seatedOnce = true;

    if (step.wait && this._met(step.wait, ctx)) { this._advance(); return; }

    // 案内板はエディタの上には出さない。段に合う画面のときだけ見せる
    const here = ctx.editorOpen ? 'editor' : ctx.worldOpen ? 'world' : 'game';
    const show = here === step.where;
    if (this.el.classList.contains('gone') === show) {
      this.el.classList.toggle('gone', !show);
      this._syncHeight();
    }
    if (show) this._focus(step.focus); else this._clearFocus();
  }

  _met(cond, ctx) {
    if (cond === 'arrived') return this.arrived;
    if (cond === 'seated') return !!ctx.seated;
    if (cond === 'launched') return (ctx.game?.launched || 0) > 0;
    if (cond === 'spun') return (ctx.game?.spins || 0) > 0 || (ctx.game?.pendingSpins || 0) > 0;
    return true;
  }

  _advance() {
    if (this.step + 1 >= STEPS.length) { this.finish(true); return; }
    this.step++;
    this._render();
  }

  _render() {
    const step = this.current;
    if (!step) return;
    this.whoEl.textContent = this._name();
    this.lineEl.innerHTML = esc(t(`tutorial.${step.id}`, {
      machine: t(`machine.${TARGET.machine}.short`),
      total: STEPS.length,
      n: this.step + 1,
    }));
    this.el.style.setProperty('--c', this._color());
    this.el.dataset.step = String(this.step + 1);
    // 待つ段は自分から進まない。押せるボタンを出すと待っていることが伝わらない
    this.nextEl.classList.toggle('gone', !!step.wait);
    this.nextEl.textContent = this.step + 1 >= STEPS.length ? t('tutorial.done') : t('tutorial.next');
    this.skipEl.textContent = t('tutorial.stop');
    this._focus(step.focus);
    this._syncHeight();
  }

  // ---------------------------------------------------------------- 指し示す

  _focus(id) {
    this._clearFocus();
    if (!id) return;
    const el = document.getElementById(id);
    const box = id === 'power' ? el?.closest('.power') : el;
    if (box) { box.classList.add('coach-focus'); this._focused = box; }
  }

  _clearFocus() {
    this._focused?.classList.remove('coach-focus');
    this._focused = null;
  }

  _name() { return t('tutorial.name'); }
  _color() { return '#7fdcaa'; }

  /** 案内で使う台。main.js が着席させるときに参照する */
  static get targetMachine() {
    return MACHINES.find((m) => m.id === TARGET.machine) || MACHINES[0];
  }
}
